/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import AccordionWrapper from '../../accordion/Accordion';
import {getAccordionTopBorderStyle} from './assetDetailsAccordionStyles';
import {DiagnosticsHeader} from './DiagnosticsHeader';
import {DiagnosticTileData} from './diagnosticTileConfig';
import {DiagnosticTileGrid} from './DiagnosticTileGrid';
import {FaultCodesSection} from './FaultCodesSection';
import {InputsRowData, InputsSection} from './InputsSection';

export interface DiagnosticsFaultCodesData {
  count: number;
  infoTooltip?: string;
  onViewDetails?: () => void;
}

export interface DiagnosticsAccordionProps {
  lastReportedAt?: string;
  tiles: DiagnosticTileData[];
  faultCodes?: DiagnosticsFaultCodesData;
  inputs?: InputsRowData[];
  defaultExpanded?: boolean;
  expanded?: boolean;
  onExpand?: (expanded: boolean) => void;
}

const useStyles = makeStyles(() => ({
  // every Asset Details Drawer accordion gets this 2px teal top border to
  // separate it from the card above it ...
  header  : getAccordionTopBorderStyle(),
  // padding now comes from AccordionWrapper's own detailsRoot (current
  // theme default: 8px 16px 24px 16px) rather than being re-applied here ...
  details : {
    boxSizing     : 'border-box',
    display       : 'flex',
    flexDirection : 'column',
    alignItems    : 'flex-start',
    width         : '100%',
    gap           : '24px'
  }
}));

/**
 * DiagnosticsAccordion is the ION-7462 Diagnostics section of the Asset
 * Details Drawer: a header (freshness pill, no info icon), the Diagnostic
 * Tile Grid, and the Fault Codes / Inputs containers, each 24px apart, per
 * the Figma "Diagnostics" export. Fault Codes and Inputs are shown "only
 * when data is available" per Details.txt: omitting `faultCodes`/passing
 * an empty `inputs` array omits that section entirely rather than
 * rendering it empty - mirroring the legacy `AssetDetailsDiagnostics`'
 * own per-field presence checks (each `Render.If` there gates on the raw
 * value being present, not a single combined "has diagnostics" flag).
 *
 * Expansion is uncontrolled via `defaultExpanded` by default; passing
 * `expanded` and `onExpand` instead hands control to the caller, which the
 * Asset Details Drawer uses to drive its header's expand/collapse-all
 * control. Both are forwarded straight to `AccordionWrapper`.
 *
 * ```typescript
 *
 *   import {DiagnosticsAccordion} from
 *     '@ion/dls/src/components/assetdetails/current/DiagnosticsAccordion';
 *   ...
 *
 *   <DiagnosticsAccordion lastReportedAt='8 Hrs Ago'
 *     tiles={[
 *       {id: 'seatbelt', label: 'SEAT BELT', value: 'Buckled', severity: 'good'},
 *       {id: 'fuel', label: 'FUEL LEVEL', value: '9', unit: '%',
 *         severity: 'critical'}
 *     ]}
 *     faultCodes={{count: 20, onViewDetails: onViewDetails}}
 *     inputs={[
 *       {id: 'blade', label: 'Blade', value: 'Off', isActive: false}
 *     ]} />
 *
 * ```
 *
 */
export const DiagnosticsAccordion = ({
  lastReportedAt,
  tiles,
  faultCodes,
  inputs,
  defaultExpanded,
  expanded,
  onExpand
}: DiagnosticsAccordionProps) => {
  const classes = useStyles();
  return (
    <AccordionWrapper isCustomStyle
      className={classes.header}
      defaultExpanded={defaultExpanded}
      expanded={expanded}
      onExpand={onExpand}
      header={<DiagnosticsHeader lastReportedAt={lastReportedAt} />}
      details={
        <div className={classes.details}>
          <DiagnosticTileGrid tiles={tiles} />
          {faultCodes && (
            <FaultCodesSection count={faultCodes.count}
              infoTooltip={faultCodes.infoTooltip}
              onViewDetails={faultCodes.onViewDetails} />
          )}
          {Boolean(inputs?.length) && (
            <InputsSection inputs={inputs} />
          )}
        </div>
      } />
  );
};

export default memo(DiagnosticsAccordion);
